import { useEffect, useState } from 'react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { makeStyles } from '@material-ui/core/styles';
import { getMe } from '../../plugins/infrastructure/api';
import { ROLE_LABELS, RbacRole } from '../../plugins/infrastructure/rbacTypes';
import { lightTokens } from '../theme/rafayTheme';

const useStyles = makeStyles(theme => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  // A plain initials avatar (not the brand mark) — matching the reference
  // design's "AS" circle. This deliberately reverses an earlier version of
  // this component that put the Control Tower logo here instead of an
  // identity avatar; the logo still appears in the sidebar (SidebarLogo).
  avatar: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: lightTokens.tealSoftBg,
    color: lightTokens.tealDark,
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  nameBlock: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
  },
  name: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  role: {
    fontSize: '0.6875rem',
    color: theme.palette.text.secondary,
  },
  userBlock: {
    padding: theme.spacing(1.5, 2, 1),
    minWidth: 200,
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
  },
  userEmail: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

interface Profile {
  displayName?: string;
  email?: string;
}

// "Atharva Something" -> "AS"; falls back to the first two letters of the
// email's local part if no display name is on file yet, and to a plain
// person glyph while still loading.
function initialsFor(profile: Profile): string {
  if (profile.displayName) {
    const parts = profile.displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  }
  if (profile.email) {
    return profile.email.slice(0, 2).toUpperCase();
  }
  return '';
}

// The topbar's user chip — same identity/sign-out behavior
// ProfileSidebarItem used to provide from the bottom of the sidebar, moved
// up here now that the sidebar's bottom slot is the "Need Help" panel
// instead. Adds the role line the sidebar version never showed.
//
// Deliberately fetches its own GET /me via getMe() instead of useRbac() —
// this component renders via AppRootWrapperBlueprint (see modules/topBar.tsx)
// as a sibling to {children}, not inside it, so it isn't guaranteed to sit
// inside whatever DOM position RbacProvider's own AppRootWrapperBlueprint
// wrapper ends up nested at. Fetching directly avoids depending on that
// ordering at all.
export const TopBarUserMenu = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [profile, setProfile] = useState<Profile>({});
  const [role, setRole] = useState<RbacRole | undefined>(undefined);

  useEffect(() => {
    getMe(fetchApi, backendUrl)
      .then(me => {
        setProfile(prev => ({
          ...prev,
          displayName: me.displayName,
          email: me.email,
        }));
        setRole(me.role);
      })
      .catch(() => {});
  }, [fetchApi, backendUrl]);

  return (
    <>
      <Box
        className={classes.trigger}
        onClick={e => setAnchor(e.currentTarget as HTMLElement)}
        role="button"
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
      >
        <div className={classes.avatar}>{initialsFor(profile)}</div>
        <Box className={classes.nameBlock}>
          {profile.displayName && (
            <Typography className={classes.name}>
              {profile.displayName}
            </Typography>
          )}
          {role && (
            <Typography className={classes.role}>
              {ROLE_LABELS[role]}
            </Typography>
          )}
        </Box>
        <ExpandMoreIcon fontSize="small" color="disabled" />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            borderRadius: 10,
            minWidth: 210,
          },
        }}
      >
        <Box className={classes.userBlock}>
          <Typography className={classes.userName}>
            {profile.displayName ?? '—'}
          </Typography>
          {profile.email && (
            <Typography className={classes.userEmail}>
              {profile.email}
            </Typography>
          )}
        </Box>

        <Divider />

        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My Profile" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchor(null);
            identityApi.signOut();
          }}
        >
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </Menu>
    </>
  );
};
